import { useCallback, useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import "react-toastify/dist/ReactToastify.css";
import { toast } from "react-toastify";
import { getFirebaseErrorMessage } from "../getFirebaseErrorMessage";
import { useUserAuth } from "../../context/UserAuthContext";
import axios from "axios";
import Swal from "sweetalert2";
import Select from "react-select";

const MobnoVerificationForm = () => {
  const { language } = useUserAuth();
  const base_url = import.meta.env.VITE_LOCAL_BASE_URL;
  const { user } = useUserAuth();
  // console.log("user", user);
  const vendorId = user?.uid;
  const [formData, setFormData] = useState({
    business_name: "",
    name: "",
    mobno: "",
  });

  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [currencies, setCurrencies] = useState([]);
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const navigate = useNavigate();

  const validateField = (name, value) => {
    const newErrors = { ...errors };
    const mobnoRegex = /^[0-9]{7,14}$/;

    switch (name) {
      case "business_name":
        if (!value.trim()) {
          newErrors.business_name =
            language === "en"
              ? "Business name is required"
              : "व्यवसाय का नाम आवश्यक है";
        } else if (value.length < 2) {
          newErrors.business_name =
            language === "en"
              ? "Business name must be at least 2 characters"
              : "व्यवसाय का नाम कम से कम 2 अक्षरों का होना चाहिए";
        } else {
          delete newErrors.business_name;
        }
        break;

      case "name":
        if (!value.trim()) {
          newErrors.name =
            language === "en" ? "Name is required" : "नाम आवश्यक है";
        } else {
          delete newErrors.name;
        }
        break;

      case "mobno":
        if (!value.trim()) {
          newErrors.mobno =
            language === "en"
              ? "Mobile number is required"
              : "मोबाइल नंबर आवश्यक है";
        } else if (!mobnoRegex.test(value.replace(/\s/g, ""))) {
          newErrors.mobno =
            language === "en"
              ? "Please enter a valid mobile number"
              : "कृपया एक वैध मोबाइल नंबर दर्ज करें";
        } else {
          delete newErrors.mobno;
        }
        break;

      default:
        break;
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    let formattedValue = value;
    if (name === "mobno") {
      formattedValue = value.replace(/\D/g, "").slice(0, 10);
    }
    setFormData((prev) => ({
      ...prev,
      [name]: formattedValue,
    }));
    validateField(name, formattedValue);
  };

  const fetchVendorData = useCallback(async () => {
    if (!vendorId) return;
    setLoading(true);
    try {
      const response = await axios.get(
        `${base_url}/api/v1/users/id/${vendorId}`
      );
      const vendorData = response.data?.data;
      setFormData({
        business_name: "",
        name: vendorData?.business_name || "",
        mobno: vendorData?.mobno || "",
      });
      setIsEditing(true);
    } catch (error) {
      console.error("Error fetching vendor data:", error);
      toast.error(
        language === "en"
          ? "Failed to load vendor data"
          : "विक्रेता डेटा लोड करने में विफल"
      );
    } finally {
      setLoading(false);
    }
  }, [vendorId, base_url, language]);

  const checkBusinessNameExists = async (businessName) => {
    if (!businessName.trim()) return false;
    try {
      const response = await axios.post(`${base_url}/api/v1/users/exist`, {
        business_name: businessName,
      });
      return response?.data?.data?.exists || false;
    } catch (error) {
      console.error("Error checking business name:", error);
      return false;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const isBusinessNameValid = validateField(
      "business_name",
      formData.business_name
    );
    const isMobnoValid = validateField("mobno", formData.mobno);
    if (!isBusinessNameValid || !isMobnoValid) {
      toast.error(
        language === "en"
          ? "Please fix the errors in the form"
          : "कृपया फॉर्म में त्रुटियों को ठीक करें"
      );
      return;
    }
    if (!isEditing) {
      const businessNameExists = await checkBusinessNameExists(
        formData.business_name
      );
      if (businessNameExists) {
        Swal.fire({
          title:
            language === "en"
              ? "Business Name Already Exists"
              : "व्यवसाय का नाम पहले से मौजूद है",
          text:
            language === "en"
              ? "Please choose a different business name."
              : "कृपया कोई अलग व्यवसाय नाम चुनें।",
          icon: "error",
          iconColor: "#d33",
          background: "#fefefe",
          color: "#333",
          showCloseButton: true,
          showConfirmButton: false,
          cancelButtonText: language === "en" ? "Close" : "बंद करें",
          cancelButtonColor: "#d33",
          customClass: {
            popup: "swal-wide",
            cancelButton: "custom-cancel-btn",
            title: "custom-title",
            htmlContainer: "custom-text",
          },
        });
        return;
      }
    }
    setLoading(true);
    try {
      const payload = {
        business_name: formData.business_name.trim(),
        name: formData.name.trim(),
        mobno: formData.mobno,
        ...(selectedCountry && { currencies_id: selectedCountry.id }),
      };
      const response = await axios.put(
        `${base_url}/api/v1/users/update-user-mobno/${vendorId}`,
        payload
      );
      console.log("API Response:", response.data);
      toast.success(
        language === "en"
          ? isEditing
            ? "Profile updated successfully!"
            : "Profile created successfully!"
          : isEditing
          ? "प्रोफाइल सफलतापूर्वक अपडेट की गई!"
          : "प्रोफाइल सफलतापूर्वक बनाई गई!"
      );
      navigate("/basic-detail");
    } catch (err) {
      console.error("API Error:", err);
      const errorMessage =
        getFirebaseErrorMessage(err.code) ||
        (language === "en"
          ? "An error occurred. Please try again."
          : "एक त्रुटि हुई। कृपया पुनः प्रयास करें।");
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };
  const fetchCurrency = useCallback(async () => {
    try {
      const response = await axios.get(`${base_url}/api/v1/currency/country`);
      const data = Array.isArray(response?.data?.data)
        ? response?.data?.data
        : [];
      setCurrencies(data);
      if (!selectedCountry) {
        const india = data.find((c) => c.country_name === "India");
        if (india) {
          setSelectedCountry({
            value: india.country_id,
            label: `${india.flag} ${india.code}`,
            code: india.code.replace("+", ""),
            flag: india.flag,
            name: india.country_name,
            id: india.id,
          });
        }
      }
    } catch (error) {
      console.error("Error fetching currencies:", error);
      toast.error(
        language === "en" ? "Failed to load countries" : "देश लोड करने में विफल"
      );
    }
  }, [selectedCountry, base_url, language]);

  useEffect(() => {
    fetchCurrency();
    if (vendorId) {
      fetchVendorData();
    }
  }, [fetchCurrency, fetchVendorData, vendorId]);

  const countryOptions = useMemo(
    () =>
      currencies.map((item) => ({
        value: item.country_id,
        label: `${item.flag} ${item.code}`,
        code: item.code.replace(/\D/g, ""),
        flag: item.flag,
        name: item.country_name,
        id: item.id,
      })),
    [currencies]
  );

  const filterOption = (option, inputValue) => {
    const input = inputValue.toLowerCase();
    return (
      option.data.name.toLowerCase().includes(input) ||
      option.data.code.includes(input) ||
      option.data.label.toLowerCase().includes(input)
    );
  };

  const isFormValid = useMemo(
    () =>
      formData.business_name.trim() &&
      formData.name.trim() &&
      formData.mobno.replace(/\D/g, "").length === 10 &&
      Object.keys(errors).length === 0,
    [formData, errors]
  );

  if (loading && vendorId) {
    return (
      <div className="d-flex justify-content-center align-items-center min-vh-100">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="d-flex min-vh-100" style={{ backgroundColor: "#f8f9fa" }}>
      <div className="container my-auto">
        <div className="row justify-content-center">
          <div className="col-md-8 col-lg-6">
            <div className="card shadow-sm rounded-lg border-0">
              <div
                className="card-header text-white text-center py-4"
                style={{
                  backgroundColor: "#085f9a",
                  borderTopLeftRadius: "12px",
                  borderTopRightRadius: "12px",
                }}
              >
                <h3 className="mb-0 fw-bold">
                  {language === "en"
                    ? isEditing
                      ? "Update Vendor Profile"
                      : "Excellent! Let's Create An Outstanding Profile"
                    : isEditing
                    ? "विक्रेता प्रोफाइल अपडेट करें"
                    : "बेहतरीन! चलिए एक उत्कृष्ट प्रोफाइल बनाते हैं"}
                </h3>
              </div>
              <div className="card-body p-5">
                <form onSubmit={handleSubmit}>
                  {/* Business Name Field */}
                  <div className="mb-4">
                    <label
                      htmlFor="business_name"
                      className="form-label fw-semibold"
                    >
                      {language === "en" ? "Business Name" : "व्यवसाय का नाम"}{" "}
                      <span className="text-danger">*</span>
                    </label>
                    <input
                      type="text"
                      id="business_name"
                      name="business_name"
                      placeholder={
                        language === "en"
                          ? "Enter your business name"
                          : "अपना व्यवसाय नाम दर्ज करें"
                      }
                      className={`form-control form-control-lg ${
                        errors.business_name ? "is-invalid" : ""
                      }`}
                      value={formData.business_name}
                      onChange={handleChange}
                      onBlur={(e) =>
                        validateField("business_name", e.target.value)
                      }
                      required
                      disabled={loading}
                    />
                    {errors.business_name && (
                      <div className="invalid-feedback d-block">
                        {errors.business_name}
                      </div>
                    )}
                  </div>

                  {/* Name Field */}
                  <div className="mb-4">
                    <label htmlFor="name" className="form-label fw-semibold">
                      {language === "en" ? "Contact Person Name" : "नाम"}{" "}
                      <span className="text-danger">*</span>
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      placeholder={
                        language === "en"
                          ? "Enter your name"
                          : "अपना नाम दर्ज करें"
                      }
                      className={`form-control form-control-lg ${
                        errors.name ? "is-invalid" : ""
                      }`}
                      value={formData.name}
                      onChange={handleChange}
                      onBlur={(e) => validateField("name", e.target.value)}
                      required
                      disabled={loading}
                    />
                    {errors.name && (
                      <div className="invalid-feedback d-block">
                        {errors.name}
                      </div>
                    )}
                  </div>
                  <div className="mb-4">
                    <label htmlFor="mobno" className="form-label fw-semibold">
                      {language === "en"
                        ? "Which number you want to receive leads ? "
                        : "मोबाइल नंबर"}
                      <small className="text-muted ms-1">
                        ({language === "en" ? "Optional" : "वैकल्पिक"})
                      </small>
                    </label>
                    <div className="input-group">
                      <Select
                        options={countryOptions}
                        value={selectedCountry}
                        onChange={setSelectedCountry}
                        menuPosition="fixed"
                        filterOption={filterOption}
                        isSearchable
                        placeholder="🌍 Search..."
                        styles={{
                          control: (provided) => ({
                            ...provided,
                            minHeight: "41px",
                            border: "1px solid #e2e8f0",
                            borderRight: "none",
                            borderRadius: "8px 0px 0px 8px",
                            boxShadow: "none",
                            "&:hover": {
                              borderColor: "#e2e8f0",
                            },
                          }),
                          input: (provided) => ({
                            ...provided,
                            padding: "0",
                            margin: "0",
                          }),
                          singleValue: (provided) => ({
                            ...provided,
                            display: "flex",
                            alignItems: "center",
                            gap: "6px",
                          }),
                          dropdownIndicator: (provided) => ({
                            ...provided,
                            padding: "0 8px",
                            color: "#64748b",
                          }),
                          indicatorSeparator: () => null,
                          menu: (provided) => ({
                            ...provided,
                            zIndex: 9999,
                          }),
                          option: (provided, state) => ({
                            ...provided,
                            backgroundColor: state.isSelected
                              ? "#f1f5f9"
                              : "white",
                            color: "#334155",
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                            "&:hover": {
                              backgroundColor: "#f8fafc",
                            },
                          }),
                        }}
                        components={{
                          IndicatorSeparator: () => null,
                        }}
                      />
                      <input
                        type="tel"
                        id="mobno"
                        name="mobno"
                        placeholder={
                          language === "en"
                            ? "Type the number where leads should reach."
                            : "मोबाइल नंबर दर्ज करें (वैकल्पिक)"
                        }
                        className={`form-control form-control-lg ${
                          errors.mobno ? "is-invalid" : ""
                        }`}
                        value={formData.mobno}
                        onChange={handleChange}
                        maxLength={10}
                      />
                    </div>
                    {errors.mobno && (
                      <div className="invalid-feedback d-block">
                        {errors.mobno}
                      </div>
                    )}
                  </div>
                  {/* Submit Button */}
                  <div className="d-grid mt-4">
                    <button
                      type="submit"
                      className="btn text-white py-3 fw-semibold rounded-lg"
                      style={{
                        backgroundColor: "#085f9a",
                        fontSize: "16px",
                      }}
                      disabled={loading || !isFormValid}
                    >
                      {loading ? (
                        <span
                          className="spinner-border spinner-border-sm me-2"
                          role="status"
                          aria-hidden="true"
                        ></span>
                      ) : null}
                      {loading
                        ? language === "en"
                          ? "Processing..."
                          : "प्रक्रिया कर रहे हैं..."
                        : isEditing
                        ? language === "en"
                          ? "Update Profile"
                          : "प्रोफाइल अपडेट करें"
                        : language === "en"
                        ? "Create Profile"
                        : "प्रोफाइल बनाएं"}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MobnoVerificationForm;
